import { Button } from "@fefyi/ui/button";
import { motion } from "motion/react";
import { ToggleLeft, ToggleRight } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

export const WhileInView = () => {
  return (
    <motion.div
      initial="outOfView"
      animate="outOfView"
      whileInView="inView"
      viewport={{ amount: 1 }}
      variants={{
        outOfView: {
          transition: {
            staggerChildren: 0.1,
            staggerDirection: -1,
          },
        },
        inView: {
          transition: {
            staggerChildren: 0.1,
          },
        },
      }}
      data-nosnippet
      className="space-between grid w-full grid-cols-4 justify-center gap-2"
    >
      {[1, 2, 3, 4].map((i) => (
        <motion.div
          key={i}
          variants={{
            outOfView: {
              opacity: 0,
              x: -30,
            },
            inView: {
              opacity: 1,
              x: 0,
            },
          }}
          className="aspect-square rounded-xl bg-background-contrast"
        />
      ))}
    </motion.div>
  );
};

export const WithAndWithoutInitial = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [useInitial, setUseInitial] = useState(false);
  const playgroundRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (playgroundRef.current) {
      const line = playgroundRef.current.querySelector(
        ".expressive-code .ec-line:nth-of-type(3) .code",
      );

      if (line) {
        line.classList.add("transition-all");
        if (!useInitial) {
          line.classList.add("line-through", "opacity-50");
        } else {
          line.classList.remove("line-through", "opacity-50");
        }
      }
    }
  }, [useInitial]);

  return (
    <div className="flex flex-col items-center gap-5">
      <motion.div
        key={useInitial ? "withInitial" : "withoutInitial"}
        initial="outOfView"
        animate="outOfView"
        whileInView="inView"
        variants={{
          outOfView: {
            transition: {
              staggerChildren: 0.1,
              staggerDirection: -1,
            },
          },
          inView: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        viewport={{ amount: 1 }}
        data-nosnippet
        className="flex w-full justify-center"
      >
        <motion.div
          variants={{
            inView: {
              opacity: 1,
              borderRadius: "50%",
              transition: {
                duration: 4,
              },
            },
            outOfView: useInitial
              ? {
                  opacity: 0,
                  borderRadius: "0%",
                }
              : {},
          }}
          className="aspect-square w-[300px] rounded-xl bg-background-contrast"
        />
      </motion.div>
      <Button className="gap-1" onClick={() => setUseInitial((u) => !u)}>
        {useInitial ? (
          <>
            <ToggleLeft />
            With initial ☝️
          </>
        ) : (
          <>
            <ToggleRight />
            Without initial ☝️
          </>
        )}
      </Button>

      <div ref={playgroundRef}>{children}</div>
    </div>
  );
};
